import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, Heart, Dumbbell, Moon, Apple, Plane, ChevronRight, Coins, Flame } from 'lucide-react';

// Nanouk images
const NANOUK = {
    puppySleeping: '/nanouk/energy_balanced.png',
    puppyAwake: '/nanouk/energy_fit.png',
    happy: '/nanouk/energy_balanced.png',
};

const content = {
    de: {
        scene1: {
            title: 'Jede große Reise beginnt mit einem ersten Schritt...',
            subtitle: '...und einem treuen Freund an deiner Seite.',
            tap: 'Tippe, um deinen Buddy zu wecken',
        },
        scene2: {
            greeting: 'Wuff! *gähn* Hey!',
            intro: 'Ich bin Nanouk. Ich habe im hohen Norden auf jemanden wie dich gewartet.',
            mission: 'Jemanden, der bereit ist, die Welt zu entdecken – und dabei richtig fit zu bleiben.',
            askName: 'Wie soll ich dich nennen, Buddy?',
            placeholder: 'Dein Name',
            continue: 'Weiter',
        },
        scene3: {
            title: 'Worauf sollen wir beide am meisten achten?',
            subtitle: 'Ich passe meine Tipps an deine Ziele an.',
            options: [
                { id: 'fitness', icon: Dumbbell, label: 'Fitness & Kraft', color: 'from-orange-500 to-red-500' },
                { id: 'sleep', icon: Moon, label: 'Besserer Schlaf', color: 'from-indigo-500 to-purple-500' },
                { id: 'nutrition', icon: Apple, label: 'Gesunde Ernährung', color: 'from-green-500 to-emerald-500' },
                { id: 'travel', icon: Plane, label: 'Fit auf Reisen', color: 'from-cyan-500 to-blue-500' },
                { id: 'all', icon: Sparkles, label: 'Alles gleichzeitig!', color: 'from-pink-500 to-violet-500' },
            ],
            continue: 'Los geht\'s!',
        },
        sceneCount: {
            title: 'Wie möchtest du zählen?',
            subtitle: 'Beides führt zum Ziel – wähle, was zu dir passt. Du kannst es jederzeit in den Einstellungen ändern.',
            options: [
                { id: 'calories', icon: Flame, label: 'Kalorien', desc: 'Der Klassiker: Ich rechne deinen Tagesbedarf aus und du behältst die kcal im Blick.', color: 'from-orange-500 to-red-500' },
                { id: 'taler', icon: Coins, label: 'Taler', desc: 'Punkte statt Kalorien: 18 Taler pro Tag. Protein verbilligt, Fett & Zucker verteuern – Sport bringt Bonus-Taler!', color: 'from-amber-400 to-yellow-600' },
            ],
            continue: 'Weiter',
        },
        scene4: {
            title: 'Perfekt!',
            message: 'Ich habe unser Lager aufgeschlagen und bin bereit für unser erstes Abenteuer.',
            ready: 'Lass uns gemeinsam durchstarten, Buddy!',
            start: 'Abenteuer starten 🐺',
        },
    },
    en: {
        scene1: {
            title: 'Every great journey begins with a single step...',
            subtitle: '...and a loyal friend by your side.',
            tap: 'Tap to wake up your buddy',
        },
        scene2: {
            greeting: 'Woof! *yawn* Hey!',
            intro: "I'm Nanouk. I've been waiting in the far north for someone like you.",
            mission: 'Someone ready to explore the world – while staying fit and healthy.',
            askName: 'What should I call you, Buddy?',
            placeholder: 'Your name',
            continue: 'Continue',
        },
        scene3: {
            title: 'What should we focus on together?',
            subtitle: "I'll tailor my tips to your goals.",
            options: [
                { id: 'fitness', icon: Dumbbell, label: 'Fitness & Strength', color: 'from-orange-500 to-red-500' },
                { id: 'sleep', icon: Moon, label: 'Better Sleep', color: 'from-indigo-500 to-purple-500' },
                { id: 'nutrition', icon: Apple, label: 'Healthy Eating', color: 'from-green-500 to-emerald-500' },
                { id: 'travel', icon: Plane, label: 'Fit While Traveling', color: 'from-cyan-500 to-blue-500' },
                { id: 'all', icon: Sparkles, label: 'All of the above!', color: 'from-pink-500 to-violet-500' },
            ],
            continue: "Let's go!",
        },
        sceneCount: {
            title: 'How do you want to count?',
            subtitle: 'Both get you there – pick what suits you. You can change this anytime in Settings.',
            options: [
                { id: 'calories', icon: Flame, label: 'Calories', desc: "The classic: I calculate your daily needs and you keep an eye on the kcal.", color: 'from-orange-500 to-red-500' },
                { id: 'taler', icon: Coins, label: 'Taler', desc: 'Points instead of calories: 18 Taler per day. Protein discounts, fat & sugar cost more – sport earns bonus Taler!', color: 'from-amber-400 to-yellow-600' },
            ],
            continue: 'Continue',
        },
        scene4: {
            title: 'Perfect!',
            message: "I've set up camp and I'm ready for our first adventure.",
            ready: "Let's crush it together, Buddy!",
            start: 'Start Adventure 🐺',
        },
    },
};

export default function Onboarding({ onComplete, userName }) {
    const { language } = useLanguage();
    const t = content[language] || content.de;
    
    const [scene, setScene] = useState(1);
    const [buddyName, setBuddyName] = useState(userName || '');
    const [selectedGoals, setSelectedGoals] = useState([]);
    const [countMethod, setCountMethod] = useState('calories');
    const [isAwake, setIsAwake] = useState(false);

    const handleWakeUp = () => {
        setIsAwake(true);
        setTimeout(() => setScene(2), 1500);
    };

    const handleGoalToggle = (goalId) => {
        if (goalId === 'all') {
            setSelectedGoals(['all']);
        } else {
            setSelectedGoals(prev => {
                const filtered = prev.filter(g => g !== 'all');
                if (filtered.includes(goalId)) {
                    return filtered.filter(g => g !== goalId);
                }
                return [...filtered, goalId];
            });
        }
    };

    const handleComplete = () => {
        onComplete({ name: buddyName, goals: selectedGoals, talerEnabled: countMethod === 'taler' });
    };

    // Snowflake animation
    const Snowflakes = () => (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-white/30 rounded-full"
                    initial={{ 
                        x: Math.random() * window.innerWidth, 
                        y: -20,
                        opacity: 0.3 + Math.random() * 0.7
                    }}
                    animate={{ 
                        y: window.innerHeight + 20,
                        x: `+=${Math.sin(i) * 100}`,
                    }}
                    transition={{ 
                        duration: 5 + Math.random() * 5,
                        repeat: Infinity,
                        delay: Math.random() * 5,
                        ease: 'linear'
                    }}
                />
            ))}
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 bg-gradient-to-b from-slate-900 via-blue-950 to-cyan-950 flex items-center justify-center overflow-hidden">
            <Snowflakes />
            
            <AnimatePresence mode="wait">
                {/* Scene 1: The Awakening - Entire scene is tappable */}
                {scene === 1 && (
                    <motion.div
                        key="scene1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="text-center px-6 max-w-lg cursor-pointer select-none"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isAwake) handleWakeUp();
                        }}
                        onTouchStart={(e) => {
                            e.stopPropagation();
                            if (!isAwake) handleWakeUp();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && !isAwake) handleWakeUp();
                        }}
                        data-testid="onboarding-scene1"
                    >
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="text-white/90 drop-shadow-md text-lg mb-2 pointer-events-none"
                        >
                            {t.scene1.title}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1 }}
                            className="text-cyan-300 text-xl font-medium mb-12 pointer-events-none"
                        >
                            {t.scene1.subtitle}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.5 }}
                            className="relative pointer-events-none"
                        >
                            <motion.div
                                animate={isAwake ? { scale: [1, 1.2, 0] } : { scale: [1, 1.05, 1] }}
                                transition={isAwake ? { duration: 0.5 } : { duration: 2, repeat: Infinity }}
                                className="w-48 h-48 mx-auto"
                            >
                                <img 
                                    src={isAwake ? NANOUK.puppyAwake : NANOUK.puppySleeping}
                                    alt="Nanouk"
                                    className="w-full h-full object-contain drop-shadow-2xl"
                                />
                            </motion.div>
                            
                            {!isAwake && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="text-cyan-200 drop-shadow text-sm mt-6"
                                >
                                    ✨ {t.scene1.tap}
                                </motion.p>
                            )}
                        </motion.div>
                    </motion.div>
                )}

                {/* Scene 2: First Meeting */}
                {scene === 2 && (
                    <motion.div
                        key="scene2"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        className="text-center px-6 max-w-lg"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', bounce: 0.5 }}
                            className="w-40 h-40 mx-auto mb-6"
                        >
                            <img 
                                src={NANOUK.puppyAwake}
                                alt="Nanouk"
                                className="w-full h-full object-contain drop-shadow-2xl"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="space-y-3 mb-8"
                        >
                            <p className="text-2xl font-bold text-foreground">{t.scene2.greeting}</p>
                            <p className="text-foreground/80">{t.scene2.intro}</p>
                            <p className="text-cyan-300">{t.scene2.mission}</p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            className="space-y-4"
                        >
                            <p className="text-foreground font-medium">{t.scene2.askName}</p>
                            <Input
                                value={buddyName}
                                onChange={(e) => setBuddyName(e.target.value)}
                                placeholder={t.scene2.placeholder}
                                className="bg-white/10 border-white/20 text-foreground placeholder:text-foreground/40 text-center text-lg"
                            />
                            <Button
                                onClick={() => setScene(3)}
                                disabled={!buddyName.trim()}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-foreground gap-2"
                            >
                                {t.scene2.continue}
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </motion.div>
                    </motion.div>
                )}

                {/* Scene 3: Goal Selection */}
                {scene === 3 && (
                    <motion.div
                        key="scene3"
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        className="text-center px-6 max-w-lg w-full"
                    >
                        <motion.div
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            className="w-24 h-24 mx-auto mb-4"
                        >
                            <img 
                                src={NANOUK.puppyAwake}
                                alt="Nanouk"
                                className="w-full h-full object-contain"
                            />
                        </motion.div>

                        <h2 className="text-xl font-bold text-foreground mb-2">{t.scene3.title}</h2>
                        <p className="text-foreground/60 text-sm mb-6">{t.scene3.subtitle}</p>

                        <div className="grid grid-cols-1 gap-3 mb-6">
                            {t.scene3.options.map((option, index) => (
                                <motion.div
                                    key={option.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Card
                                        className={`cursor-pointer transition-all ${
                                            selectedGoals.includes(option.id)
                                                ? 'ring-2 ring-cyan-400 bg-white/20'
                                                : 'bg-white/5 hover:bg-white/10'
                                        }`}
                                        onClick={() => handleGoalToggle(option.id)}
                                    >
                                        <CardContent className="p-4 flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center`}>
                                                <option.icon className="w-6 h-6 text-foreground" />
                                            </div>
                                            <span className="text-foreground font-medium flex-1 text-left">{option.label}</span>
                                            {selectedGoals.includes(option.id) && (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                                                >
                                                    <Heart className="w-4 h-4 text-foreground fill-white" />
                                                </motion.div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <Button
                            onClick={() => setScene(3.5)}
                            disabled={selectedGoals.length === 0}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-foreground gap-2"
                        >
                            {t.scene3.continue}
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </motion.div>
                )}

                {/* Scene 3.5: Counting method (calories vs Taler) */}
                {scene === 3.5 && (
                    <motion.div
                        key="scene35"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className="text-center px-6 max-w-md w-full"
                        data-testid="onboarding-count-scene"
                    >
                        <h2 className="text-xl font-bold text-foreground mb-2">{t.sceneCount.title}</h2>
                        <p className="text-foreground/60 text-sm mb-6">{t.sceneCount.subtitle}</p>

                        <div className="grid grid-cols-1 gap-3 mb-6">
                            {t.sceneCount.options.map((option, index) => (
                                <motion.div
                                    key={option.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Card
                                        className={`cursor-pointer transition-all ${
                                            countMethod === option.id
                                                ? 'ring-2 ring-cyan-400 bg-white/20'
                                                : 'bg-white/5 hover:bg-white/10'
                                        }`}
                                        onClick={() => setCountMethod(option.id)}
                                        data-testid={`onboarding-count-${option.id}`}
                                    >
                                        <CardContent className="p-4 flex items-start gap-4">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center shrink-0`}>
                                                <option.icon className="w-6 h-6 text-foreground" />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <span className="text-foreground font-medium block">{option.label}</span>
                                                <span className="text-foreground/60 text-xs leading-snug block mt-0.5">{option.desc}</span>
                                            </div>
                                            {countMethod === option.id && (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-1"
                                                >
                                                    <Heart className="w-4 h-4 text-foreground fill-white" />
                                                </motion.div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <Button
                            onClick={() => setScene(4)}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-foreground gap-2"
                            data-testid="onboarding-count-continue"
                        >
                            {t.sceneCount.continue}
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </motion.div>
                )}

                {/* Scene 4: Ready to Start */}
                {scene === 4 && (
                    <motion.div
                        key="scene4"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center px-6 max-w-lg"
                    >
                        <motion.div
                            initial={{ y: -50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ type: 'spring', bounce: 0.4 }}
                            className="w-48 h-48 mx-auto mb-6"
                        >
                            <img 
                                src={NANOUK.happy}
                                alt="Nanouk"
                                className="w-full h-full object-contain drop-shadow-2xl"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="space-y-4 mb-8"
                        >
                            <h2 className="text-3xl font-bold text-foreground">{t.scene4.title}</h2>
                            <p className="text-foreground/80 text-lg">
                                Hey <span className="text-cyan-300 font-bold">{buddyName}</span>!
                            </p>
                            <p className="text-foreground/70">{t.scene4.message}</p>
                            <p className="text-cyan-300 font-medium text-lg">{t.scene4.ready}</p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <Button
                                onClick={handleComplete}
                                size="lg"
                                className="w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 hover:from-cyan-600 hover:via-blue-600 hover:to-violet-600 text-foreground text-lg py-6 gap-2"
                            >
                                <Sparkles className="w-5 h-5" />
                                {t.scene4.start}
                            </Button>
                        </motion.div>

                        {/* Celebration particles */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                            {[...Array(15)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-3 h-3 rounded-full"
                                    style={{
                                        background: ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'][i % 4],
                                        left: `${Math.random() * 100}%`,
                                    }}
                                    initial={{ y: '100vh', opacity: 0 }}
                                    animate={{ 
                                        y: '-100vh',
                                        opacity: [0, 1, 1, 0],
                                        x: Math.sin(i) * 50,
                                    }}
                                    transition={{
                                        duration: 3,
                                        delay: i * 0.1,
                                        repeat: Infinity,
                                        repeatDelay: 2,
                                    }}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
